import React, { useState } from 'react';
import { ArrowLeft, User, Camera } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/Button';
import { useTheme } from '../contexts/ThemeContext';
interface NameEntryScreenProps {
  onBack: () => void;
  onContinue: (name: string) => void;
}
export function NameEntryScreen({
  onBack,
  onContinue
}: NameEntryScreenProps) {
  const {
    isLight
  } = useTheme();
  const [name, setName] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const handleContinue = () => {
    if (name.trim()) {
      onContinue(name.trim());
    }
  };
  return <div className="h-full flex flex-col p-6 relative overflow-hidden" data-id="element-3295" data-mp="2cpnwikktcq31">
      {/* Background orb */}
      <div className={`absolute top-32 -left-16 w-48 h-48 rounded-full blur-3xl pointer-events-none ${isLight ? 'bg-vexu/15' : 'bg-vexu/10'}`} data-id="element-3296" data-mp="2hoqop4fp31tl" />

      {/* Header */}
      <div className="flex items-center mb-8 relative z-10" data-id="element-3297" data-mp="fwucdslpdnxr">
        <button onClick={onBack} className={`p-2 -ml-2 rounded-full border transition-colors ${isLight ? 'text-slate-500 hover:text-slate-900 bg-white/60 backdrop-blur-md border-slate-200' : 'text-zinc-400 hover:text-white bg-white/5 backdrop-blur-md border-white/10'}`} data-id="element-3298" data-mp="ftyhkd55jfdj">
          <ArrowLeft className="w-5 h-5" data-id="element-3299" data-mp="3ied4p0iemzls" />
        </button>
      </div>

      <div className="flex-1 flex flex-col pt-4 relative z-10" data-id="element-3300" data-mp="3ddz7yem97c32">
        {/* Avatar placeholder */}
        <motion.div initial={{
        scale: 0.8,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        duration: 0.4,
        ease: 'easeOut'
      }} className="flex justify-center mb-8" data-id="element-3301" data-mp="2poelvmuszypk">
          <div className="relative" data-id="element-3302" data-mp="dyc11aki2gfw">
            <div className={`w-24 h-24 rounded-full flex items-center justify-center border-2 transition-colors duration-300 ${isLight ? 'bg-slate-100 border-slate-200' : 'bg-dark-card border-white/10'}`} data-id="element-3303" data-mp="dxapl4zknrxn">
              <User className={`w-10 h-10 ${isLight ? 'text-slate-300' : 'text-zinc-600'}`} data-id="element-3304" data-mp="32qz5bcj73xx0" />
            </div>
            <button className={`absolute -bottom-1 -right-1 w-8 h-8 rounded-full flex items-center justify-center border-2 transition-colors ${isLight ? 'bg-vexu text-white border-white shadow-md' : 'bg-vexu text-white border-dark shadow-lg shadow-vexu/20'}`} data-id="element-3305" data-mp="3cpodgdlai402">
              <Camera className="w-3.5 h-3.5" data-id="element-3306" data-mp="2ozu1rzcpilhe" />
            </button>
          </div>
        </motion.div>

        <motion.div initial={{
        y: 20,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} transition={{
        duration: 0.4,
        delay: 0.15
      }} data-id="element-3307" data-mp="2t1nyxxnp17ct">
          <h1 className={`text-3xl font-bold mb-3 text-center ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-3308" data-mp="2185dcu30ah5q">
            What's your name?
          </h1>
          <p className={`text-base mb-8 text-center ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-3309" data-mp="1xrsh4c19tmd8">
            This is how your AI assistant will greet callers on your behalf.
          </p>
        </motion.div>

        <motion.div initial={{
        y: 20,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} transition={{
        duration: 0.4,
        delay: 0.25
      }} className="w-full space-y-8" data-id="element-3310" data-mp="1jsjao42va6mg">
          <div className={`backdrop-blur-xl border rounded-2xl p-6 transition-colors duration-500 ${isLight ? 'bg-white/60 border-slate-200/50 shadow-sm' : 'bg-dark-card/60 border-white/10 shadow-xl shadow-black/50'}`} data-id="element-3311" data-mp="153ddelnlaqh7">
            <label className={`text-xs font-medium uppercase tracking-wider mb-3 block ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-3312" data-mp="w2f9s4h2ckz5">
              Your name
            </label>
            <input type="text" value={name} onChange={e => setName(e.target.value)} onFocus={() => setIsFocused(true)} onBlur={() => setIsFocused(false)} placeholder="e.g. Alex Johnson" autoFocus onKeyDown={e => {
            if (e.key === 'Enter' && name.trim()) handleContinue();
          }} className={`w-full text-lg font-medium bg-transparent border-b-2 pb-3 outline-none transition-all duration-300 placeholder:font-normal ${isLight ? `text-slate-900 placeholder:text-slate-300 ${isFocused ? 'border-vexu' : 'border-slate-200'}` : `text-white placeholder:text-zinc-600 ${isFocused ? 'border-vexu' : 'border-white/10'}`}`} data-id="element-3313" data-mp="2r6ws8z79n3ef" />
          </div>

          <Button fullWidth size="lg" onClick={handleContinue} disabled={!name.trim()} data-id="element-3314" data-mp="3sw0bxptwi9pz">
            Continue
          </Button>

          <p className={`text-xs text-center leading-relaxed ${isLight ? 'text-slate-400' : 'text-zinc-600'}`} data-id="element-3315" data-mp="1m7zt9k2kauza">
            Your AI will say things like "Hi, you've reached{' '}
            <span className={`font-medium ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-3316" data-mp="1vykrh6ebpxho">
              {name.trim() || '...'}
            </span>
            's line. How can I help?"
          </p>
        </motion.div>
      </div>
    </div>;
}